import React, { useMemo } from 'react'
import {
    Box,
    Chip,
    MenuItem,
    Select,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined'

import {
    WorkbookBucketId,
    WorkbookSectionsState,
    isWorkbookSectionConfigured,
    listWorkbookSections,
} from '../PipelineFlowDemo/workbookSectionDraft'
import { PHASE_PANEL_TONES, PhaseControlPanel } from './PhaseControlPanel'

export type PhaseApplicantBinding = {
    available: WorkbookBucketId[]
    required: WorkbookBucketId[]
}

export type PhaseApplicantBindingsMap = Partial<
    Record<string, PhaseApplicantBinding>
>

type SectionOption = {
    id: WorkbookBucketId
    label: string
}

type ChipTone = 'available' | 'required'

const useStyles = makeStyles((theme: Theme) => ({
    hint: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.4,
    },
    row: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.4),
        width: '100%',
    },
    labelRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(0.5),
        minHeight: 28,
    },
    rowLabel: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    chipRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        minHeight: 22,
    },
    chip: {
        height: 24,
        fontSize: '0.75rem',
        fontWeight: 600,
    },
    chipAvailable: {
        backgroundColor: '#E3F2FD',
        color: '#1565C0',
        border: '1px solid #90CAF9',
        '& .MuiChip-deleteIcon': {
            color: '#1565C0',
        },
    },
    chipRequired: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '& .MuiChip-deleteIcon': {
            color: theme.palette.primary.contrastText,
        },
    },
    chipInheritedAvailable: {
        backgroundColor: '#EEF5FB',
        color: '#547FA8',
        border: '1px dashed #90CAF9',
        opacity: 0.92,
    },
    chipInheritedRequired: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        opacity: 0.72,
        border: `1px dashed ${theme.palette.primary.dark}`,
    },
    emptyChipRow: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    addSelect: {
        minWidth: 140,
        maxWidth: 200,
        fontSize: '0.75rem',
        backgroundColor: theme.palette.common.white,
        '& .MuiSelect-root': {
            paddingTop: 6,
            paddingBottom: 6,
            fontSize: '0.75rem',
        },
    },
}))

function emptyBinding(): PhaseApplicantBinding {
    return { available: [], required: [] }
}

export function accumulateUpstreamBindings(
    phaseIds: string[],
    phaseIndex: number,
    bindings: PhaseApplicantBindingsMap,
): { available: Set<string>; required: Set<string> } {
    const available = new Set<string>()
    const required = new Set<string>()
    for (let i = 0; i < phaseIndex; i += 1) {
        const binding = bindings[phaseIds[i]] ?? emptyBinding()
        for (const id of binding.available) available.add(String(id))
        for (const id of binding.required) {
            required.add(String(id))
            available.add(String(id))
        }
    }
    return { available, required }
}

export function configuredSectionOptions(
    sections: WorkbookSectionsState | undefined,
): SectionOption[] {
    return listWorkbookSections(sections)
        .filter((section) =>
            isWorkbookSectionConfigured(sections, section.bucket_id),
        )
        .map((section) => ({
            id: section.bucket_id,
            label:
                section.kind === 'custom'
                    ? `${section.label} (custom)`
                    : section.label,
        }))
}

function optionsFromIds(
    ids: Iterable<string>,
    byId: Map<string, SectionOption>,
): SectionOption[] {
    const result: SectionOption[] = []
    for (const id of Array.from(ids)) {
        const option = byId.get(String(id))
        if (option) result.push(option)
    }
    return result
}

function SectionMultiSelect({
    label,
    inherited,
    local,
    addable,
    chipTone,
    readOnly,
    onChangeLocal,
}: {
    label: string
    inherited: SectionOption[]
    local: SectionOption[]
    addable: SectionOption[]
    chipTone: ChipTone
    readOnly?: boolean
    onChangeLocal: (next: SectionOption[]) => void
}) {
    const classes = useStyles()
    const localIds = new Set(local.map((option) => String(option.id)))

    const remove = (id: WorkbookBucketId) => {
        if (readOnly) return
        onChangeLocal(local.filter((option) => option.id !== id))
    }

    const add = (id: string) => {
        if (readOnly || !id) return
        const option = addable.find((item) => String(item.id) === id)
        if (!option || localIds.has(String(option.id))) return
        onChangeLocal([...local, option])
    }

    const localChipClass =
        chipTone === 'required' ? classes.chipRequired : classes.chipAvailable
    const inheritedChipClass =
        chipTone === 'required'
            ? classes.chipInheritedRequired
            : classes.chipInheritedAvailable

    const hasChips = inherited.length > 0 || local.length > 0

    return (
        <Box className={classes.row}>
            <Box className={classes.labelRow}>
                <Typography className={classes.rowLabel}>{label}</Typography>
                {addable.length > 0 && !readOnly ? (
                    <Select
                        className={classes.addSelect}
                        displayEmpty
                        value=""
                        onChange={(e) => add(String(e.target.value))}
                        variant="outlined"
                        margin="dense"
                    >
                        <MenuItem value="" disabled>
                            Add…
                        </MenuItem>
                        {addable.map((option) => (
                            <MenuItem key={option.id} value={String(option.id)}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                ) : null}
            </Box>
            <Box className={classes.chipRow}>
                {!hasChips ? (
                    <Typography className={classes.emptyChipRow}>
                        None selected
                    </Typography>
                ) : (
                    <>
                        {inherited.map((option) => (
                            <Chip
                                key={`inherited-${option.id}`}
                                className={`${classes.chip} ${inheritedChipClass}`}
                                size="small"
                                label={option.label}
                                title="Set in an earlier phase"
                            />
                        ))}
                        {local.map((option) => (
                            <Chip
                                key={`local-${option.id}`}
                                className={`${classes.chip} ${localChipClass}`}
                                size="small"
                                label={option.label}
                                onDelete={
                                    readOnly
                                        ? undefined
                                        : () => remove(option.id)
                                }
                                disabled={readOnly}
                            />
                        ))}
                    </>
                )}
            </Box>
        </Box>
    )
}

export const PhaseApplicantDataBindings: React.FC<{
    phaseId: string
    phaseIds: string[]
    phaseIndex: number
    bindings: PhaseApplicantBindingsMap
    sections: WorkbookSectionsState | undefined
    applicantDataSelected: boolean
    readOnly?: boolean
    onChange: (phaseId: string, next: PhaseApplicantBinding) => void
}> = ({
    phaseId,
    phaseIds,
    phaseIndex,
    bindings,
    sections,
    applicantDataSelected,
    readOnly,
    onChange,
}) => {
    const classes = useStyles()
    const allConfigured = useMemo(() => configuredSectionOptions(sections), [
        sections,
    ])
    const byId = useMemo(() => {
        const map = new Map<string, SectionOption>()
        for (const option of allConfigured) map.set(String(option.id), option)
        return map
    }, [allConfigured])

    const upstream = useMemo(
        () => accumulateUpstreamBindings(phaseIds, phaseIndex, bindings),
        [bindings, phaseIds, phaseIndex],
    )

    const current = bindings[phaseId] ?? emptyBinding()

    // Local picks only — never store upstream-inherited ids on this phase.
    const localAvailable = current.available
        .map((id) => byId.get(String(id)))
        .filter((option): option is SectionOption => {
            if (!option) return false
            return !upstream.available.has(String(option.id))
        })

    const localRequired = current.required
        .map((id) => byId.get(String(id)))
        .filter((option): option is SectionOption => {
            if (!option) return false
            return !upstream.required.has(String(option.id))
        })

    const inheritedAvailable = optionsFromIds(upstream.available, byId)
    const inheritedRequired = optionsFromIds(upstream.required, byId)

    const availableAddable = allConfigured.filter((option) => {
        const id = String(option.id)
        if (upstream.available.has(id)) return false
        return !localAvailable.some((item) => String(item.id) === id)
    })

    const requiredAddable = allConfigured.filter((option) => {
        const id = String(option.id)
        if (upstream.required.has(id)) return false
        return !localRequired.some((item) => String(item.id) === id)
    })

    const panel = (children: React.ReactNode) => (
        <PhaseControlPanel
            title="Applicant data"
            icon={<AssignmentOutlinedIcon />}
            tone={PHASE_PANEL_TONES.applicant}
        >
            {children}
        </PhaseControlPanel>
    )

    if (!applicantDataSelected) {
        return panel(
            <Typography className={classes.hint}>
                Select Applicant Data in Setup to bind sections here.
            </Typography>,
        )
    }

    if (allConfigured.length === 0) {
        return panel(
            <Typography className={classes.hint}>
                Configure at least one section in Setup → Applicant Data.
            </Typography>,
        )
    }

    const setAvailable = (nextOptions: SectionOption[]) => {
        if (readOnly) return
        const nextAvailable = nextOptions.map((option) => option.id)
        const availableIds = new Set([
            ...Array.from(upstream.available),
            ...nextAvailable.map(String),
        ])
        const nextRequired = localRequired
            .map((option) => option.id)
            .filter((id) => availableIds.has(String(id)))
        onChange(phaseId, {
            available: nextAvailable,
            required: nextRequired,
        })
    }

    const setRequired = (nextOptions: SectionOption[]) => {
        if (readOnly) return
        const nextRequired = nextOptions.map((option) => option.id)
        const availableSet = new Set(localAvailable.map((o) => String(o.id)))
        const nextAvailable = localAvailable.map((option) => option.id)
        for (const id of nextRequired) {
            const key = String(id)
            if (!upstream.available.has(key) && !availableSet.has(key)) {
                nextAvailable.push(id)
                availableSet.add(key)
            }
        }
        onChange(phaseId, {
            available: nextAvailable,
            required: nextRequired,
        })
    }

    return panel(
        <>
            <Typography className={classes.hint}>
                Applicants only see sections marked available by this phase.
                Leave a section available from the start unless you want to hide
                it until later. Required sections accumulate through this step —
                anything required earlier still must be finished here.
            </Typography>
            <SectionMultiSelect
                label="Available here"
                inherited={inheritedAvailable}
                local={localAvailable}
                addable={availableAddable}
                chipTone="available"
                readOnly={readOnly}
                onChangeLocal={setAvailable}
            />
            <SectionMultiSelect
                label="Required to advance"
                inherited={inheritedRequired}
                local={localRequired}
                addable={requiredAddable}
                chipTone="required"
                readOnly={readOnly}
                onChangeLocal={setRequired}
            />
        </>,
    )
}
